export interface RequestOptions extends RequestInit {
  queryParams?: Record<string, string | number | boolean | undefined>;
  responseType?: ResponseType;
}

export enum ResponseType {
  Json = 'json',
  Text = 'text',
}

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/+$/, ''); // trim trailing slashes
  }

  private buildUrl(path: string, queryParams?: Record<string, string | number | boolean | undefined>): string {
    const fullPath = `${this.baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;

    // Support relative URLs
    const isAbsolute = /^https?:\/\//i.test(fullPath);
    const url = new URL(fullPath, isAbsolute ? undefined : window.location.origin);

    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }
    return url.toString();
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { queryParams, responseType, ...fetchOptions } = options;
    const url = this.buildUrl(path, queryParams);

    const response = await fetch(url, {
      headers: {
        'Content-Type': responseType == ResponseType.Text ? 'text/plain' : 'application/json',
        ...fetchOptions.headers,
      },
      ...fetchOptions,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API error (${response.status}): ${errorText}`);
    }
  
    if (responseType === ResponseType.Text) {
      return response.text() as Promise<T>;
    }

    return response.json() as Promise<T>;
  }

  public get<T, Q extends Record<string, string | number | boolean | undefined> = Record<string, string | number | boolean | undefined>>(path: string, queryParams?: Q, responseType?: ResponseType): Promise<T> {
    return this.request<T>(path, { method: 'GET', queryParams, responseType });
  }

  public post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
}